import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/studentAuthContext";
import { 
  Loader2, 
  CheckCircle2, 
  Languages, 
  Activity, 
  ChevronRight 
} from "lucide-react";

import {
  HiOutlineColorSwatch, HiOutlineCalculator, HiOutlineBookOpen,
  HiOutlineDatabase, HiOutlineChip, HiOutlineChartBar,
  HiOutlineTerminal, HiOutlineShieldCheck, HiMusicNote,
  HiCamera, HiGlobeAlt, HiAcademicCap, HiVideoCamera, HiLightBulb
} from "react-icons/hi";
import { HiOutlineWrench } from "react-icons/hi2";

// Importing your logo
import Logo from "../assets/images/logo.png";

const translations = {
  English: { explore: "Choose Your Interests", available: "App Language", continue: "Continue", select: "Select a course", total: "Selected" },
  Hausa: { explore: "Zaɓi Sha'awa", available: "Harshen App", continue: "Ci gaba", select: "Zaɓi kwas", total: "An zaɓa" },
  Igbo: { explore: "Họrọ Ihe Masịrị Gị", available: "Asụsụ App", continue: "Gaa n'ihu", select: "Họrọ otu", total: "Họrọ" },
  Yoruba: { explore: "Yan Ohun Ti O Nife", available: "Ede App", continue: "Tẹsiwaju", select: "Yan ọkan", total: "Ti yan" },
  German: { explore: "Interessen wählen", available: "App Sprache", continue: "Weiter", select: "Kurs wählen", total: "Gewählt" }
};

export default function CourseRegistration() {
  const navigate = useNavigate();
  const { student: authStudent } = useAuth();
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [language, setLanguage] = useState("English");
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");

  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_BASE_URL ||
    "http://localhost:5000";

  const student = useMemo(() => {
    if (authStudent?.id) return authStudent;
    try {
      const raw = localStorage.getItem("student");
      return raw ? JSON.parse(raw) : null;
    } catch (_err) {
      return null;
    }
  }, [authStudent]);

  const t = translations[language];

  useEffect(() => {
    const fetchPreferenceOptions = async () => {
      setLoadingOptions(true);
      setStatusMessage("");
      try {
        const res = await axios.get(`${API_BASE_URL}/api/students/preference-options`);
        const rows = res.data?.data || [];
        const options = rows
          .map((row, index) => ({
            id: row.id || index + 1,
            name: row.name,
            description: row.description || "",
          }))
          .filter((item) => item.name);
        setCourses(options);
        if (options.length === 0) {
          setStatusMessage("No preferences configured yet. Please contact admin.");
        }
      } catch (_err) {
        setCourses([]);
        setStatusMessage("Failed to load preferences from backend.");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchPreferenceOptions();
  }, [API_BASE_URL]);

  const getCourseAppearance = (name) => {
    const label = String(name || "").toLowerCase();

    if (label.includes("design") || label.includes("ui") || label.includes("ux")) {
      return { icon: <HiOutlineColorSwatch />, color: "text-pink-500" };
    }
    if (label.includes("math")) {
      return { icon: <HiOutlineCalculator />, color: "text-purple-500" };
    }
    if (label.includes("english") || label.includes("language")) {
      return { icon: <HiOutlineBookOpen />, color: "text-emerald-500" };
    }
    if (label.includes("backend") || label.includes("database")) {
      return { icon: <HiOutlineDatabase />, color: "text-blue-500" };
    }
    if (label.includes("ai") || label.includes("artificial")) {
      return { icon: <HiOutlineChip />, color: "text-amber-500" };
    }
    if (label.includes("marketing") || label.includes("analysis")) {
      return { icon: <HiOutlineChartBar />, color: "text-rose-500" };
    }
    if (label.includes("python") || label.includes("code") || label.includes("program")) {
      return { icon: <HiOutlineTerminal />, color: "text-cyan-600" };
    }
    if (label.includes("cyber") || label.includes("security")) {
      return { icon: <HiOutlineShieldCheck />, color: "text-slate-600" };
    }
    if (label.includes("music")) {
      return { icon: <HiMusicNote />, color: "text-indigo-500" };
    }
    if (label.includes("photo")) {
      return { icon: <HiCamera />, color: "text-orange-500" };
    }
    if (label.includes("video")) {
      return { icon: <HiVideoCamera />, color: "text-red-500" };
    }
    if (label.includes("german") || label.includes("french") || label.includes("spanish")) {
      return { icon: <HiGlobeAlt />, color: "text-yellow-600" };
    }
    if (label.includes("vocational") || label.includes("skill")) {
      return { icon: <HiOutlineWrench />, color: "text-orange-600" };
    }
    if (label.includes("aptitude")) {
      return { icon: <HiLightBulb />, color: "text-teal-500" };
    }
    if (label.includes("fashion")) {
      return { icon: <HiAcademicCap />, color: "text-fuchsia-500" };
    }

    return { icon: <HiOutlineBookOpen />, color: "text-blue-500" };
  };

  const toggleCourse = (name) => {
    setSelectedCourses((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };

  const handleContinue = async () => {
    if (selectedCourses.length === 0 || loadingOptions) return;

    if (!student?.id) {
      navigate("/login");
      return;
    }

    setLoading(true);
    setStatusMessage("");

    try {
      const form = new FormData();
      form.append("preference", JSON.stringify(selectedCourses));

      await axios.put(`${API_BASE_URL}/api/students/profile/${student.id}`, form);

      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (_err) {
      setStatusMessage("Could not save preferences. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center font-sans antialiased">
      
      {/* PROFESSIONAL LOGO PRELOADER */}
      <AnimatePresence>
        {loading && (
          <motion.div
            className="fixed inset-0 bg-white z-[100] flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-8">
                <motion.img 
                  src={Logo} 
                  alt="Logo" 
                  className="w-20 h-20 object-contain relative z-10"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
                <motion.div 
                  className="absolute inset-0 bg-blue-100 rounded-full blur-2xl"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              </div>

              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-3 px-5 py-2 bg-gray-50 rounded-full border border-gray-100">
                  <Activity className="w-4 h-4 text-blue-600 animate-pulse" />
                  <span className="text-sm font-bold text-gray-700 tracking-tight uppercase">Saving your interests</span>
                </div>
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 text-gray-300 animate-spin" />
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em]">Redirecting...</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STICKY HEADER */}
      <div className="w-full bg-white/80 backdrop-blur-xl sticky top-0 z-30 px-6 py-5 border-b border-gray-50">
        <div className="max-w-md mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src={Logo} alt="Logo" className="w-8 h-8 object-contain" />
            <div>
              <h1 className="text-lg font-black text-gray-900 leading-tight tracking-tight">{t.explore}</h1>
              <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{selectedCourses.length} {t.total}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 px-3 py-1.5 rounded-2xl shadow-sm">
            <Languages className="w-3.5 h-3.5 text-gray-400" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent outline-none text-[11px] font-bold text-gray-800 appearance-none cursor-pointer"
            >
              {Object.keys(translations).map(lang => <option key={lang}>{lang}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* MINIMALIST GRID */}
      <div className="max-w-md w-full px-5 pt-8 pb-48">
        {statusMessage && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
            {statusMessage}
          </div>
        )}

        {loadingOptions ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-6 text-center text-sm font-semibold text-gray-500">
            Loading preference options...
          </div>
        ) : (
        <div className="flex flex-wrap gap-3 justify-center">
          {courses.map(course => {
            const isSelected = selectedCourses.includes(course.name);
            const appearance = getCourseAppearance(course.name);
            return (
              <motion.button
                key={course.id}
                whileTap={{ scale: 0.95 }}
                onClick={() => toggleCourse(course.name)}
                className={`
                  flex items-center gap-3 px-5 py-3 rounded-2xl transition-all duration-200 border
                  ${isSelected 
                    ? `bg-gray-900 border-gray-900 shadow-xl shadow-gray-200` 
                    : `bg-white border-gray-100 hover:border-gray-300`}
                `}
              >
                <span className={`${isSelected ? "text-blue-400" : appearance.color} text-xl`}>
                  {appearance.icon}
                </span>
                <span className={`text-[13px] font-bold tracking-tight ${isSelected ? "text-white" : "text-gray-700"}`}>
                  {course.name}
                </span>
                {isSelected && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                    <CheckCircle2 className="w-4 h-4 text-white fill-blue-500 border-none" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
        )}
      </div>

      {/* FOOTER ACTION AREA */}
      <div className="fixed bottom-0 w-full p-8 z-40 bg-gradient-to-t from-white via-white to-transparent pointer-events-none">
        <div className="max-w-md mx-auto pointer-events-auto">
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white p-2 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] border border-gray-100"
          >
            <button
              onClick={handleContinue}
              disabled={selectedCourses.length === 0 || loadingOptions || loading}
              className={`
                w-full py-5 rounded-[2rem] font-black text-sm transition-all duration-300
                flex items-center justify-center gap-3 tracking-widest uppercase
                ${selectedCourses.length > 0 && !loadingOptions
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"}
              `}
            >
              <span>{selectedCourses.length > 0 ? t.continue : t.select}</span>
              {selectedCourses.length > 0 && <ChevronRight className="w-4 h-4" />}
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}